"use client";
import Image from "next/image";
import ProductSlider from "../../category/_components/ProductSlider/ProductSlider";
import { useEffect, useState } from "react";
import useCart from "@/hooks/useCart";
import useCheckout from "@/hooks/useCheckout";
import { usePathname, useRouter } from "next/navigation";
import toast from "react-hot-toast";
import { useDictionary } from "@/components/DictProvider/DictProvider";
import ProductReview from "./ProductReview";
import CustomNavLink from "@/components/CustomLink/CustomNavLink";
import WishListLoader from "@/components/Loading/WishListLoader";
import ProductSelection from "./ProductSelection";
import CategoryChips from "./CategoryChips";
import AgeConsent from "../../../../../components/AgeConsent/AgeConsent";
import { useLoggedIn } from "@/store/useAuthStore";
import { isUnder21 } from "@/lib/isUnder21";
import YouTubeViewer from "./youtube-viewer";
import usePost from "@/hooks/usePost";
import { trackFbAndCAPI, pushDataLayer, metaId } from "@/lib/fbPixel";
const ProductDetails = ({ sProduct }: { sProduct: any }) => {
  const dict = useDictionary();
  const { PostMethod } = usePost();
  const { addtCartList, cart } = useCart();
  const router = useRouter();
  const pathName = usePathname();
  const [qty, setQty] = useState(1);
  // Editable string mirror of qty for the typed-quantity input. Held
  // separately so the user can clear the field while typing without
  // qty jumping to NaN; committed on blur / Enter.
  const [qtyInput, setQtyInput] = useState("1");
  useEffect(() => {
    setQtyInput(qty.toString());
  }, [qty]);

  const handleQtyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQtyInput(e.target.value.replace(/[^0-9]/g, ""));
  };

  const handleQtyInputBlur = () => {
    const parsed = parseInt(qtyInput, 10);
    const maxQty = sProduct?.data?.stock ?? 0;
    if (!qtyInput || isNaN(parsed) || parsed < 1) {
      setQtyInput(qty.toString());
      return;
    }
    if (parsed > maxQty) {
      setQty(maxQty);
      setQtyInput(maxQty.toString());
      toast.success(`Adjusted to max available: ${maxQty}`);
      return;
    }
    setQty(parsed);
  };

  const handleQtyInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  };
  const { moveCart } = useCheckout();
  const [thumbnailImage, setThumbnailImage] = useState(null);
  // Age Consent =============================================
  const { auth, profile } = useLoggedIn();
  const [modalOpen, setModalOpen] = useState(false);
  const [whoRun, setWhoRun] = useState<(() => void) | null>(null);

  const handleOpenModal = (runMainFunc: any) => {
    if (!auth) {
      setModalOpen(true);
      setWhoRun(() => runMainFunc);
      return;
    }

    if (!profile?.DOB) {
      setModalOpen(true);
      setWhoRun(() => runMainFunc);
      return;
    }

    if (isUnder21(profile.DOB)) {
      setWhoRun(() => runMainFunc);
      setModalOpen(true);
      return;
    }

    // ✅ Only when logged in, DOB exists, and age >= 21
    runMainFunc();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setWhoRun(null);
  };

  const handleConfirm = () => {
    setModalOpen(false);
    if (whoRun) {
      whoRun();
      setWhoRun(null);
    }
  };

  const handleCheckout = async () => {
    try {
      let response = await moveCart(sProduct?.data?.id, "product", qty);
      if (response?.success && response?.data?.cartId) {
        sessionStorage.setItem("CID", response?.data?.cartId);
        sessionStorage.setItem("CID_TIMESTAMP", Date.now().toString());
        router.push(
          `/${pathName.split("/")[1]}/checkout/${response?.data?.cartId}`
        );
      } else {
        toast.error(response?.message || "Something went wrong.");
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong.");
    }
  };

  // Meta Pixel: ViewContent + GA4 view_item
  useEffect(() => {
    if (sProduct?.data) {
      const p = sProduct.data;
      const category = p?.categoryMappings?.[0]?.category;
      trackFbAndCAPI("ViewContent", {
        content_ids: [metaId(p.barcode, p.id)],
        content_name: p.language?.product,
        ...(category && { content_category: category }),
        content_type: "product",
        value: p.sellingPrice,
        currency: "KWD",
      });
      pushDataLayer("view_item", {
        ecommerce: {
          currency: "KWD",
          value: p.sellingPrice,
          items: [
            {
              item_id: p.id,
              item_name: p.language?.product,
              item_brand: p.language?.brand,
              price: p.sellingPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  }, [sProduct?.data?.id]);

  const isCarted = (productId: any) => {
    return cart?.items?.some((item: any) => item?.product?.id === productId);
  };

  const handleColorSelect = async (variant: any) => {
    const { productSlug, color, size } = variant;

    // Base URL starts with the productSlug
    let url = productSlug;

    // Array to hold query parameters
    const params: string[] = [];

    // Add size if it exists
    if (size && size !== "") {
      params.push(`size=${encodeURIComponent(size)}`);
    }

    // Add color if it's a valid object
    if (
      color &&
      !Array.isArray(color) &&
      typeof color === "object" &&
      color?.name
    ) {
      params.push(`color=${encodeURIComponent(color.name)}`);
    }

    // Append query parameters if there are any
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    // Navigate to the constructed URL
    router.replace(url);
  };

  // Parent variant docs carry barcode "-----" and don't list their own slug
  // among their variants. Adding such a doc to the cart records the parent
  // _id, which then appears on invoices with no real barcode. Hide the
  // purchase buttons while we're still on the parent — ProductSelection's
  // auto-select will navigate to a real variant shortly after mount.
  const isVariantParent =
    sProduct?.data?.composition?.trim() === "Product with Variants" &&
    sProduct?.data?.barcode === "-----";

  return (
    <div className="">
      <div className="md:relative w-full grid grid-cols-1 md:grid-cols-2 gap-7">
        <div
          className={`px-2 md:px-10 md:pb-20 py-4 max-h-min md:sticky md:top-[150px]`}
        >
          <div
            className="pb-4 flex justify-start items-center flex-wrap gap-y-1"
            style={{ direction: "ltr" }}
          >
            <div
              onClick={() => router.back()}
              className="flex items-center w-fit cursor-pointer"
            >
              <p className="mr-4">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.25 11.25H20.25C20.4489 11.25 20.6397 11.329 20.7803 11.4697C20.921 11.6103 21 11.8011 21 12C21 12.1989 20.921 12.3897 20.7803 12.5303C20.6397 12.671 20.4489 12.75 20.25 12.75H5.25C5.05109 12.75 4.86032 12.671 4.71967 12.5303C4.57902 12.3897 4.5 12.1989 4.5 12C4.5 11.8011 4.57902 11.6103 4.71967 11.4697C4.86032 11.329 5.05109 11.25 5.25 11.25Z"
                    fill="black"
                  />
                  <path
                    d="M5.56038 12L11.7809 18.219C11.9217 18.3598 12.0008 18.5508 12.0008 18.75C12.0008 18.9491 11.9217 19.1401 11.7809 19.281C11.64 19.4218 11.449 19.5009 11.2499 19.5009C11.0507 19.5009 10.8597 19.4218 10.7189 19.281L3.96888 12.531C3.89903 12.4613 3.84362 12.3785 3.80581 12.2874C3.768 12.1963 3.74854 12.0986 3.74854 12C3.74854 11.9013 3.768 11.8036 3.80581 11.7125C3.84362 11.6214 3.89903 11.5386 3.96888 11.469L10.7189 4.71897C10.8597 4.57814 11.0507 4.49902 11.2499 4.49902C11.449 4.49902 11.64 4.57814 11.7809 4.71897C11.9217 4.8598 12.0008 5.05081 12.0008 5.24997C12.0008 5.44913 11.9217 5.64014 11.7809 5.78097L5.56038 12Z"
                    fill="black"
                  />
                </svg>
              </p>
              <p className="text-base font-semibold">
                {dict.cancellationPage.goBack}
              </p>
            </div>
            <div className="ltr:ml-4 rtl:mr-4">
              <CategoryChips mappings={sProduct?.data?.categoryMappings} />
            </div>
          </div>
          <ProductSlider
            product={sProduct}
            picture={sProduct?.data?.pictures}
            thumbnailImage={thumbnailImage}
          />
        </div>
        <div className={`max-w-full lg:w-4/5 px-4 md:px-10 md:pb-20 md:pt-4`}>
          <div className="flex justify-between items-start ">
            <div>
              <p className=" text-own-black text-base lg:text-[20px] font-semibold">
                {sProduct?.data?.language?.brand}
              </p>
              <h3 className="text-xl  lg:text-3xl font-semibold mt-1 mb-3">
                {sProduct?.data?.language?.product}
              </h3>
            </div>
            <WishListLoader id={sProduct?.data?.id} isProduct={true} />
          </div>

          <div className="flex items-center gap-x-2">
            <div className="flex gap-x-1">
              <Image
                src={"/images/star.svg"}
                width={27}
                height={27}
                alt="rating"
              />
              <span className="font-semibold text-xl">
                {sProduct?.data?.rating?.rating > 0
                  ? sProduct?.data?.rating?.rating
                  : 4.5}
              </span>
            </div>
            <div className=" text-own-black">
              <p className="text-xl">
                (
                {sProduct?.data?.rating?.totalUsers > 0
                  ? sProduct?.data?.rating?.totalUsers
                  : 112}{" "}
                {dict.poductDescriptionPage.ratings})
              </p>
            </div>
          </div>

          {!sProduct?.data?.bogo &&
            (sProduct?.data?.strikedPrice > 0 ||
              sProduct?.data?.percentage > 0) && (
              <div
                className={`flex gap-3 items-center justify-start mt-4 md:mb-1`}
              >
                {!sProduct?.data?.percentage &&
                  !!sProduct?.data?.strikedPrice && (
                    <p className="text-gray-700 md:text-lg line-through">
                      {sProduct?.data?.strikedPrice?.toFixed(3)} KWD
                    </p>
                  )}

                {!!sProduct?.data?.percentage &&
                  !!sProduct?.data?.strikedPrice && (
                    <>
                      <p className="text-gray-700 md:text-lg line-through">
                        {sProduct?.data?.strikedPrice?.toFixed(3)} KWD
                      </p>
                      <span className="w-fit bg-green-100 text-green-800 md:text-lg font-semibold px-[6px] py-[3px] rounded">
                        {sProduct?.data?.percentage}% OFF
                      </span>
                    </>
                  )}
              </div>
            )}

          <p className="font-semibold text-xl lg:text-[24px] mt-2">
            {parseFloat(sProduct?.data?.sellingPrice)?.toFixed(3)} KWD
          </p>

          {sProduct?.data?.composition?.trim() === "Product with Variants" &&
            sProduct?.data?.colors?.length > 0 && (
              <ProductSelection
                variants={sProduct?.data?.colors}
                title={sProduct?.data?.language?.productAttributeName}
                onVariantSelect={handleColorSelect}
                setThumbnailImage={setThumbnailImage}
              />
            )}
          <div className="mt-4">
            {sProduct?.data?.stock > 0 && (
              <>
                {" "}
                <h3 className="text-sm lg:text-lg font-semibold text-black text-nowrap">
                  {dict.poductDescriptionPage.selectQuantity}
                </h3>
                <div className="mt-3  w-28 flex items-center">
                  <button
                    disabled={qty === 1}
                    onClick={() => setQty((pre) => (pre > 1 ? pre - 1 : pre))}
                    className=" flex justify-center items-center h-[28px] w-[28px]  disabled:bg-primary/80 text-white bg-primary rounded-md"
                  >
                    <svg
                      width="13"
                      height="3"
                      viewBox="0 0 13 3"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.5879 2.5H0.587891V0.5H12.5879V2.5Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={qtyInput}
                    onChange={handleQtyInputChange}
                    onBlur={handleQtyInputBlur}
                    onKeyDown={handleQtyInputKeyDown}
                    maxLength={4}
                    aria-label={dict.poductDescriptionPage.selectQuantity}
                    className="w-[40px] mx-1 text-center text-sm border border-gray-300 rounded-md outline-none bg-transparent focus:border-primary"
                  />
                  <button
                    disabled={qty >= (sProduct?.data?.stock ?? 0)}
                    onClick={() =>
                      setQty((pre) =>
                        pre < (sProduct?.data?.stock ?? 0) ? pre + 1 : pre
                      )
                    }
                    className=" flex justify-center items-center h-[28px] w-[28px]  text-white bg-primary disabled:bg-primary/80 rounded-md"
                  >
                    <svg
                      width="12"
                      height="13"
                      viewBox="0 0 12 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 7.35714H6.85714V12.5H5.14286V7.35714H0V5.64286H5.14286V0.5H6.85714V5.64286H12V7.35714Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
              </>
            )}

            {sProduct?.data?.stock > 0 ? (
              sProduct?.data?.lowStocksAlert >= sProduct?.data?.stock && (
                <p className="text-own-black text-xs lg:text-lg mt-2">
                  {`${dict.poductDescriptionPage.only} ${sProduct?.data?.stock} ${dict.poductDescriptionPage.itemsLeftInStock} `}
                </p>
              )
            ) : (
              <p className="text-own-black text-xs lg:text-lg mt-2">
                {dict.poductDescriptionPage.outOfStockDesc}
              </p>
            )}
          </div>

          <div className="mt-[32px] w-full  sticky md:static bottom-0 bg-white md:py-0 flex gap-x-4 justify-between py-2 sm:justify-start">
            {sProduct?.data?.stock > 0 ? (
              !isVariantParent && (
                <div className="flex justify-center border border-secondary rounded-md w-[50%] lg:w-[152px] h-[44px] bg-primary ">
                  <button
                    className="text-white text-lg text-font-semibold"
                    onClick={() => {
                      if (sProduct?.data?.restricted) {
                        handleOpenModal(() => handleCheckout());
                      } else {
                        handleCheckout();
                      }
                    }}
                  >
                    {dict.poductDescriptionPage.buyNow}
                  </button>
                </div>
              )
            ) : (
              <div className="flex justify-center border border-secondary rounded-md w-full h-[44px] bg-secondary-border ">
                <button
                  disabled={true}
                  className="text-black text-lg text-font-semibold"
                >
                  {dict.poductDescriptionPage.outOfStock}
                </button>
              </div>
            )}
            {sProduct?.data?.stock > 0 && !isVariantParent && (
              <div
                onClick={() => {
                  const productId = sProduct?.data?.id;
                  const restricted = sProduct?.data?.restricted;

                  if (!productId) return;

                  if (isCarted(productId)) {
                    router.push(`/${pathName.split("/")[1]}/cart`);
                    return;
                  }

                  const meta = {
                    price: sProduct?.data?.sellingPrice
                      ? Number(sProduct.data.sellingPrice)
                      : undefined,
                    name: sProduct?.data?.language?.product,
                    brand: sProduct?.data?.language?.brand,
                    category: sProduct?.data?.categoryMappings?.[0]?.category,
                    barcode: sProduct?.data?.barcode,
                  };
                  if (restricted) {
                    handleOpenModal(() => addtCartList(productId, qty, meta)); // ✅ pass function
                  } else {
                    addtCartList(productId, qty, meta); // ✅ unrestricted, run directly
                  }
                }}
                className="cursor-pointer flex justify-center border-2 border-primary rounded-md w-[50%] lg:w-[152px] h-[44px]"
              >
                <button className="text-primary text-lg font-semibold">
                  {isCarted(sProduct?.data?.id)
                    ? dict.poductDescriptionPage.goToCart
                    : dict.poductDescriptionPage.addtocart}
                </button>
              </div>
            )}
          </div>

          <div className="my-6 h-[1.5px] w-full bg-secondary-fill"></div>
          <div>
            <div className=" mb-2 grid grid-cols-2 text-base ">
              <p className="text-sm lg:text-lg font-semibold">
                {dict.poductDescriptionPage.availability}
              </p>
              <p className="text-sm lg:text-lg text-own-black">
                {dict.poductDescriptionPage.inStock}
              </p>
            </div>
            <div className=" grid grid-cols-2 text-base">
              <p className="text-sm lg:text-lg font-semibold">
                {dict.poductDescriptionPage.sku}
              </p>
              <p className="text-sm lg:text-lg text-own-black">
                {sProduct?.data?.sKU}
              </p>
            </div>
          </div>

          <div className="my-6 h-[1.5px] w-full bg-secondary-fill"></div>

          {sProduct?.data?.language?.specification &&
            sProduct?.data?.language?.specification[0] !== "" && (
              <div className="max-w-full">
                <h3 className=" text-base lg:text-[22px] font-bold mb-2">
                  {dict.poductDescriptionPage.productDescription}
                </h3>
                <div
                  className="font-medium unorder"
                  dangerouslySetInnerHTML={{
                    __html: sProduct?.data?.language?.specification[0],
                  }}
                ></div>
              </div>
            )}

          {sProduct?.data?.language?.technicalInformation?.length > 0 && (
            <div className="pt-16 max-w-full">
              <h3 className="text-base lg:text-[22px] font-bold mb-4">
                {dict.poductDescriptionPage.technicalInformation}
              </h3>
              <table className="w-full">
                <tbody className="bg-white">
                  {sProduct?.data?.language?.technicalInformation?.map(
                    (
                      detail: { name: string; value: string },
                      index: number
                    ) => (
                      <tr key={index} className="border-y border-[#bbbfbf]">
                        <td className="py-1 px-3 font-medium text-[#565959] bg-[#f0f2f2] md:text-nowrap">
                          {detail.name}
                        </td>
                        <td className="py-1 px-3 text-[#565959]">
                          {detail.value}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}

          {sProduct.data?.youtubeURL?.length > 0 && (
            <div className="my-6">
              <h1 className="text-base lg:text-[22px] font-bold mb-4">
                {dict.poductDescriptionPage.youtubeVideos}
              </h1>
              <YouTubeViewer youtubeURLs={sProduct.data?.youtubeURL} />
            </div>
          )}
        </div>
      </div>

      <AgeConsent
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />

      {/* ==================== PRODUCT REVIEW ============================= */}
      {sProduct?.data?.rating ? (
        <ProductReview productId={sProduct?.data?.id} />
      ) : (
        <div className="max-w-5xl mx-auto p-6">
          <div className="flex justify-end items-center mb-8">
            {/* <h2 className="text-xl font-semibold">
              {dict.poductDescriptionPage.ratingsAndReviews}
            </h2> */}
            <CustomNavLink
              href={`rating-and-review/${sProduct?.data?.id}`}
              className="text-primary hover:underline font-semibold"
            >
              {dict.poductDescriptionPage.rateProduct}
            </CustomNavLink>
          </div>
          <div className="flex justify-center items-center mb-12 w-full">
            <p className="font-semibold">
              {dict.poductDescriptionPage.beFirstReviewer}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
